import React, { useEffect, useState } from 'react';
import PlusIcon from '@mui/icons-material/Add';
import cs from 'classnames';

import { useAppSelector } from '../../store/hooks';
import { PaginationFooter } from '../modules/PaginationFooter';
import UpdateParticipantModal from './UpdateParticipantModal';
import { SortOptions, SortType } from '../../enums/SORT';
import { GROUP_WITH_SUBGROUP } from '../../enums/TYPES';
import LinearProgress from '../modules/LinearProgress';
import { ContainedButton } from '../modules/Buttons';
import useDebounce from '../../helpers/useDebounce';
import DeleteGroupModal from './DeleteGroupModal';
import CreateGroupModal from './CreateGroupModal';
import SearchInput from '../modules/SearchInput';
import { ROLES } from '../../enums/ROLES';
import Divider from '../modules/Divider';
import OrgGroupCard from './OrgGroupCard';
import styles from './OrgGroups.module.scss';
import { getGroupsWithOrganizations } from '../../api';

const OrgGroups = () => {
  const { role } = useAppSelector((state) => state.user);

  const [showModalEdit, setShowModalEdit] = useState(false);
  const [sortOption, setSortOption] = useState(SortOptions.NAME);
  const [sort, setSort] = useState(SortType.ASC);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOrgId, setOrgId] = useState<string>('');
  const [searchText, setSearch] = useState('');

  const debouncedValue = useDebounce<string>(searchText, 500);
  const [trigger, setTrigger] = useState(0);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    localStorage.setItem('groups_pagination_rows', e.target.value);
    setLimit(parseInt(e.target.value, 10));
    setPage(0);
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearch(e.target.value);
  };

  const switchEditModal = () => {
    setShowModalEdit(!showModalEdit);
  };

  const switchCreateModal = () => {
    setShowCreateModal(!showCreateModal);
  };

  const switchDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const toDelete = (id: string) => {
    setOrgId(id);
    switchDeleteModal();
  };

  const [groups, setGroups] = useState<GROUP_WITH_SUBGROUP[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await getGroupsWithOrganizations(searchText, page, limit);
      if (res.documents) {
        setGroups(res.documents);
        setTotal(res.total);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const paginationFromLocalStorage = localStorage.getItem('groups_pagination_rows');
    if (paginationFromLocalStorage) {
      setLimit(parseInt(paginationFromLocalStorage, 10));
    }

    fetchGroups();
  }, [debouncedValue, sort, sortOption, page, limit, trigger]);

  const [groupToEdit, setGroupToEdit] = useState<GROUP_WITH_SUBGROUP>();
  const onEditSelect = (group: GROUP_WITH_SUBGROUP) => {
    setGroupToEdit(group);
    switchEditModal();
  };

  const sortHanlder = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    if (option) {
      setSort(option.order);
      // @ts-ignore
      setSortOption(option.value);
    } else {
      if (sortOption === singleValue) {
        const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
        setSort(sortT);
      } else {
        setSort(SortType.ASC);
      }
      setSortOption(singleValue);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.link}>
        <div className={styles.linkEl}>Organisation Groups Management</div>
      </div>
      <div className="mb-5">
        <Divider />
      </div>
      <div className={styles.mainContent}>
        <div className="mb-10">
          <div className={styles.titleContainer}>
            <div className={styles.innerTitle}>Groups Management</div>
            <ContainedButton style={{ display: 'flex', alignItems: 'center' }} onClick={switchCreateModal}>
              <PlusIcon style={{ marginRight: '8px' }} fontSize="small" /> <span>Create Group</span>
            </ContainedButton>
          </div>
          <div className={styles.toolbar}>
            <SearchInput onChange={onSearch} className={styles.search} />
          </div>
        </div>
        <div className={cs('mb-5', styles.gridContainer)}>
          <div className={styles.groupHeader}>
            <div className={styles.title}>Name</div>
            <div className={styles.details}>Details</div>
            <div
              className={styles.created}
              style={{
                width: role === ROLES.ORGANIZATION_ADMIN ? '20%' : '32%',
                justifyContent: role === ROLES.ORGANIZATION_ADMIN ? 'start' : 'end',
                paddingRight: role === ROLES.ORGANIZATION_ADMIN ? '0' : '32px',
              }}
            >
              Created Date
            </div>
            <div
              className={styles.updated}
              style={{
                width: role === ROLES.ORGANIZATION_ADMIN ? '20%' : '33%',
                justifyContent: role === ROLES.ORGANIZATION_ADMIN ? 'start' : 'end',
                paddingRight: role === ROLES.ORGANIZATION_ADMIN ? '0' : '32px',
              }}
              onClick={() => sortHanlder(SortOptions.UPDATED_AT)}
            >
              Last Edit Date{' '}
            </div>

            {role === ROLES.ORGANIZATION_ADMIN && <div className={styles.action}>Action</div>}
          </div>

          <div className={styles.groupList}>
            {loading && <LinearProgress />}
            {!loading && groups.length === 0 && (
              <div className={styles.noFoldersContainer}>
                <div className={styles.noFoldersText}>No Groups</div>
                <div className="emptyStateHint">Click create group button to add your first group!</div>
              </div>
            )}
            {groups.map((group, i) => (
              <OrgGroupCard
                toDelete={toDelete}
                onEdit={onEditSelect}
                data={group}
                key={`${i}_${group.id}_${Date.now()}`}
                setFetchTrigger={setTrigger}
              />
            ))}
          </div>
        </div>
      </div>
      {groupToEdit && (
        <UpdateParticipantModal group={groupToEdit} switchModal={switchEditModal} open={showModalEdit} setFetchTrigger={setTrigger} />
      )}
      <CreateGroupModal open={showCreateModal} switchModal={switchCreateModal} setFetchTrigger={setTrigger} />
      <DeleteGroupModal id={deleteOrgId} switchModal={switchDeleteModal} open={showDeleteModal} setFetchTrigger={setTrigger} />
      <PaginationFooter total={total} page={page} rowsPerPage={limit} onRowsPerPageChange={onRowsChange} onPageChange={onPaginate} />
    </div>
  );
};

export default OrgGroups;
